import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "gestiondek_cookie_consent_v1";

/**
 * Bannière de consentement aux cookies essentiels.
 * Gestion Dek n'utilise QUE des cookies strictement nécessaires (session
 * d'authentification), donc cette bannière sert d'information et de
 * conformité RGPD. Pas d'opt-out possible : si l'utilisateur refuse,
 * il ne peut pas se connecter (les cookies de session sont indispensables).
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const accepted = localStorage.getItem(STORAGE_KEY);
      if (!accepted) {
        // Léger délai pour ne pas saturer l'écran au tout premier rendu
        const t = setTimeout(() => setVisible(true), 800);
        return () => clearTimeout(t);
      }
    } catch {
      setVisible(true);
    }
  }, []);

  const accept = () => {
    try {
      localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    } catch {
      // localStorage indisponible (mode privé) — pas grave
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[150] p-3 sm:p-4"
      role="dialog"
      aria-label="Information cookies"
      data-testid="cookie-banner"
    >
      <div
        className="max-w-4xl mx-auto rounded-2xl shadow-[8px_8px_0_0_#000] p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3"
        style={{ background: "var(--surface)", border: "1px solid var(--border-strong)" }}
      >
        <div className="shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
             style={{ background: "var(--surface-3)", border: "1px solid var(--border-strong)" }}>
          <Cookie size={18} strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="label mb-1">Cookies essentiels uniquement</div>
          <p className="text-xs text-[var(--text)] leading-relaxed">
            Gestion Dek utilise uniquement des cookies strictement nécessaires pour
            ta connexion (session sécurisée httpOnly). Aucun cookie publicitaire,
            de tracking ou d&apos;analytics.{" "}
            <Link to="/confidentialite" className="underline hover:text-black font-semibold">
              En savoir plus
            </Link>
            .
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={accept}
            className="btn-primary inline-flex items-center gap-1.5 text-xs whitespace-nowrap"
            data-testid="cookie-accept-btn"
          >
            J&apos;ai compris
          </button>
          <button
            type="button"
            onClick={accept}
            className="p-1.5 rounded-md hover:bg-[var(--surface-3)]"
            aria-label="Fermer"
            data-testid="cookie-close-btn"
          >
            <X size={16} strokeWidth={2.2} />
          </button>
        </div>
      </div>
    </div>
  );
}
